import React, { useMemo } from 'react';
import { useDrop } from 'react-dnd';
import Card, { ItemTypes } from './Card';
import { validateRequirement, validatePhase } from '../utils/phaseValidator';

// ── Typography helpers ──────────────────────────────────────────────────────
const FONT_DISPLAY = '"Cinzel", Georgia, serif';
const FONT_ANTIQUE = '"IM Fell English SC", Georgia, serif';

function PhaseBuilder({
  phaseNumber,
  phaseInfo,
  groups,
  onAddCard,
  onAddCardAtPosition, // New: for inserting at specific position in runs
  onRemoveCard,
  onClearAll,
  onUndo, // Undo last action
  canUndo = false, // Whether undo is available
  onPhaseOut,
  canPhaseOut = true, // Whether phase out button should be enabled (your turn + play phase)
  disabled = false, // Whether all interaction is disabled
  onZoneClick // NEW: Allows parent to handle clicks on the drop zone
}) {
  // Validate each group (allowing extra cards that would be valid hits)
  const groupValidations = useMemo(() =>
    groups.map((group, idx) => {
      if (group.length === 0) return { valid: false, reason: 'Empty group' };
      return validateRequirement(group, phaseInfo.requirements[idx], true);
    }),
    [groups, phaseInfo.requirements]
  );

  // Overall phase validation (allowing extra cards)
  const phaseValidation = useMemo(() => {
    if (groups.some(g => g.length === 0)) {
      return { valid: false, reason: 'All groups must have cards' };
    }
    return validatePhase(groups, phaseNumber, true);
  }, [groups, phaseNumber]);

  const hasAnyCards = groups.some(g => g.length > 0);

  // Check if a requirement is a run type
  const isRunType = (req) => {
    return req.type === 'run' ||
      req.type === 'colorRun' ||
      req.type === 'oddRun' ||
      req.type === 'evenRun' ||
      req.type === 'oddColorRun' ||
      req.type === 'evenColorRun';
  };

  return (
    <div
      style={{
        padding: '4px 0',
        transition: 'all 0.3s ease',
      }}
    >
      {/* Action buttons centered above/below drops */}
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>

        {hasAnyCards && (
          <button
            onClick={onClearAll}
            disabled={disabled}
            style={{
              fontFamily: FONT_ANTIQUE,
              fontSize: '0.72rem',
              letterSpacing: '0.08em',
              color: 'rgba(245,234,210,0.5)',
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: '5px',
              padding: '4px 14px',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={e => { e.currentTarget.style.color = '#f5ead2'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = 'rgba(245,234,210,0.5)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)'; }}
          >
            Clear All
          </button>
        )}

        {/* Phase Out button */}
        <button
          onClick={onPhaseOut}
          disabled={!phaseValidation.valid || !canPhaseOut || disabled}
          style={{
            fontFamily: FONT_DISPLAY,
            fontSize: '0.8rem',
            fontWeight: '700',
            letterSpacing: '0.1em',
            padding: '6px 20px',
            borderRadius: '6px',
            border: 'none',
            cursor: phaseValidation.valid && canPhaseOut ? 'pointer' : 'not-allowed',
            background: phaseValidation.valid && canPhaseOut
              ? 'linear-gradient(135deg, #1a7a3a 0%, #27ae60 50%, #1a7a3a 100%)'
              : 'rgba(255,255,255,0.07)',
            color: phaseValidation.valid && canPhaseOut ? '#f5ead2' : 'rgba(245,234,210,0.3)',
            boxShadow: phaseValidation.valid && canPhaseOut
              ? '0 0 14px rgba(39,174,96,0.5), inset 0 1px 0 rgba(255,255,255,0.15)'
              : 'none',
            animation: phaseValidation.valid && canPhaseOut ? 'glowPulse 1.8s ease-in-out infinite' : 'none',
            transition: 'all 0.25s ease',
          }}
        >
          Phase Out!
        </button>
      </div>

      {/* Drop zones */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center' }}>
        {phaseInfo.requirements.map((req, groupIdx) => (
          <BuilderDropZone
            key={groupIdx}
            groupIndex={groupIdx}
            requirement={req}
            cards={groups[groupIdx]}
            validation={groupValidations[groupIdx]}
            onDropCard={(card) => onAddCard(card, groupIdx)}
            onDropCardAtPosition={onAddCardAtPosition ? (card, pos) => onAddCardAtPosition(card, groupIdx, pos) : null}
            onRemoveCard={(cardId) => onRemoveCard(cardId, groupIdx)}
            isRunType={isRunType(req)}
            disabled={disabled}
            onZoneClick={onZoneClick ? () => onZoneClick(groupIdx) : null}
          />
        ))}
      </div>

      {/* Status messages */}
      {!canPhaseOut && !disabled && hasAnyCards && phaseValidation.valid && (
        <p style={{
          textAlign: 'center',
          fontFamily: FONT_ANTIQUE,
          fontSize: '0.72rem',
          letterSpacing: '0.06em',
          color: '#d4a843',
          marginTop: '8px',
          opacity: 0.8,
        }}>
          ◆ Wait for your turn to phase out ◆
        </p>
      )}

      {hasAnyCards && !phaseValidation.valid && (
        <p style={{
          textAlign: 'center',
          fontFamily: FONT_ANTIQUE,
          fontSize: '0.72rem',
          letterSpacing: '0.04em',
          color: 'rgba(231,180,80,0.8)',
          marginTop: '8px',
        }}>
          {phaseValidation.reason}
        </p>
      )}
    </div>
  );
}

// Drop zone for a single group in the builder - mobile-optimized
function BuilderDropZone({
  groupIndex,
  requirement,
  cards,
  validation,
  onDropCard,
  onDropCardAtPosition,
  onRemoveCard,
  isRunType,
  disabled,
  onZoneClick
}) {
  // Main drop zone (for general drops, adds to end)
  const [{ isOver, canDrop }, drop] = useDrop(() => ({
    accept: ItemTypes.CARD,
    canDrop: () => !disabled,
    drop: (item, monitor) => {
      // Only handle drop if not dropped on a specific position zone
      if (!monitor.didDrop()) {
        onDropCard(item.cards || item.card);
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver({ shallow: true }),
      canDrop: monitor.canDrop()
    })
  }), [onDropCard, disabled]);

  const isEmpty = cards.length === 0;
  const isValid = validation?.valid;
  const neededCards = Math.max(0, requirement.count - cards.length);

  // Border / background states
  const borderColor = isOver && canDrop
    ? '#d4a843'
    : isValid
      ? 'rgba(39,174,96,0.7)'
      : isEmpty
        ? 'rgba(255,255,255,0.2)'
        : 'rgba(231,180,80,0.4)';

  const bgColor = isOver && canDrop
    ? 'rgba(212,168,67,0.12)'
    : isValid
      ? 'rgba(39,174,96,0.07)'
      : 'rgba(255,255,255,0.03)';

  return (
    <div
      ref={drop}
      onClick={onZoneClick}
      style={{
        padding: '8px 10px',
        borderRadius: '8px',
        border: `1.5px ${isEmpty ? 'dashed' : 'solid'} ${borderColor}`,
        background: bgColor,
        minWidth: '100px',
        transition: 'all 0.2s ease',
        transform: isOver && canDrop ? 'scale(1.04)' : 'scale(1)',
        cursor: onZoneClick ? 'pointer' : 'default',
      }}
    >
      {/* Group header */}
      <div style={{ textAlign: 'center', marginBottom: '6px' }}>
        {/* Label */}
        <span style={{
          fontFamily: '"Cinzel", Georgia, serif',
          fontSize: '0.68rem',
          fontWeight: '700',
          letterSpacing: '0.12em',
          color: isValid ? '#27ae60' : '#d4a843',
          textTransform: 'uppercase',
        }}>
          {requirement.label || `Group ${groupIndex + 1}`}
        </span>
        {/* Count progress */}
        <span style={{
          fontFamily: '"IM Fell English SC", Georgia, serif',
          fontSize: '0.68rem',
          color: isValid ? 'rgba(39,174,96,0.9)' : 'rgba(245,234,210,0.55)',
          marginLeft: '5px',
        }}>
          {cards.length}/{requirement.count}
        </span>
      </div>

      {/* Cards with insertion points for runs */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0px', justifyContent: 'center', minHeight: '64px', alignItems: 'center' }}>
        {isRunType && cards.length > 0 && onDropCardAtPosition && (
          <InsertionDropZone
            position={0}
            groupIndex={groupIndex}
            onDrop={(card) => onDropCardAtPosition(card, 0)}
            disabled={disabled}
          />
        )}

        {cards.map((card, cardIdx) => (
          <React.Fragment key={card.id}>
            <div className="relative group">
              <Card card={card} draggable={!disabled} fromBuilder={true} builderGroupIndex={groupIndex} />
              <button
                onClick={() => onRemoveCard(card.id)}
                disabled={disabled}
                className="absolute -top-1 -right-1 w-4 h-4 bg-red-600 rounded-full text-white text-[10px]
                           opacity-0 group-hover:opacity-100 sm:group-hover:opacity-100 transition-opacity flex items-center justify-center
                           hover:bg-red-500 touch-manipulation"
                style={{ opacity: cards.length > 0 ? undefined : 0 }}
              >
                ×
              </button>
            </div>

            {/* Insertion point after each card for runs */}
            {isRunType && onDropCardAtPosition && (
              <InsertionDropZone
                position={cardIdx + 1}
                groupIndex={groupIndex}
                onDrop={(card) => onDropCardAtPosition(card, cardIdx + 1)}
                disabled={disabled}
              />
            )}
          </React.Fragment>
        ))}

        {/* Placeholder slots for remaining cards needed */}
        {neededCards > 0 && (
          <div style={{ display: 'flex', gap: '0px' }}>
            {Array.from({ length: neededCards }).map((_, i) => (
              <div
                key={`placeholder-${i}`}
                className="w-12 h-16 sm:w-16 sm:h-24"
                style={{
                  border: `1px dashed ${isOver && canDrop ? '#d4a843' : 'rgba(255,255,255,0.15)'}`,
                  borderRadius: '8px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: isOver && canDrop ? '#d4a843' : 'rgba(255,255,255,0.18)',
                  fontSize: '1rem',
                }}
              >
                +
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Small drop zone for inserting cards at specific positions in runs
function InsertionDropZone({ position, groupIndex, onDrop, disabled }) {
  const [{ isOver, canDrop }, drop] = useDrop(() => ({
    accept: ItemTypes.CARD,
    canDrop: () => !disabled,
    drop: (item) => {
      onDrop(item.cards || item.card);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop()
    })
  }), [onDrop, disabled, position, groupIndex]);

  return (
    <div
      ref={drop}
      style={{
        width: isOver && canDrop ? '40px' : '16px',
        margin: isOver && canDrop ? '0 4px' : '0 -8px',
        height: '64px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: isOver && canDrop ? 'rgba(212,168,67,0.35)' : 'transparent',
        borderRadius: '4px',
        transition: 'all 0.15s ease',
        cursor: 'default',
        zIndex: 20,
      }}
      title={`Insert at position ${position + 1}`}
    >
      {isOver && canDrop && (
        <span style={{ color: '#d4a843', fontSize: '0.75rem', fontWeight: '700' }}>+</span>
      )}
    </div>
  );
}

export default PhaseBuilder;
